import type { z } from 'zod';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import type { NormalizedProvidedDetails, ReportSchema } from '@ballerine/common';

import { GoogleMapsView } from '@/components/templates/shared/AddressComponents/GoogleMapsView';
import { markdownComponents } from '../../../report-shared/components/markdown-components';
import { DFMerchantDetailsContent } from './DFMerchantDetailsContent';
import { ProvidedDetailsContent } from './ProvidedDetailsContent';

type MerchantDetailsSectionProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'] | null;
  providedDetails?: NormalizedProvidedDetails | null;
  matchReason?: string | null;
  googleMapsApiKey: string;
};

export const MerchantDetails: React.FC<MerchantDetailsSectionProps> = ({
  merchantDetails,
  providedDetails,
  matchReason,
  googleMapsApiKey,
}) => (
  <div className="space-y-4 rounded-2xl bg-white pt-10">
    <div className="flex gap-6">
      <div className="flex w-3/5 flex-col">
        <h2 className="mb-3 text-xl font-semibold text-slate-800">Operating Company Details</h2>
        <DFMerchantDetailsContent merchantDetails={merchantDetails} />
      </div>
      <div className="w-2/5">
        <GoogleMapsView
          address={merchantDetails?.address || providedDetails?.address || undefined}
          countryCode={merchantDetails?.country || providedDetails?.country || undefined}
          googleMapsApiKey={googleMapsApiKey}
          className="h-80"
        />
      </div>
    </div>

    {matchReason && (
      <div className="rounded-3xl border border-gray-200 p-6 shadow-md">
        <p className="font-bold text-slate-800">Why This Company Was Identified?</p>
        <div className="text-sm leading-relaxed">
          <ReactMarkdown components={markdownComponents}>{matchReason}</ReactMarkdown>
        </div>
      </div>
    )}

    <div className="rounded-3xl border border-[#eeeded] bg-[#fbfcff] px-8 py-6 shadow-[0px_2px_4px_0px_rgba(174,174,174,0.1)]">
      <h3 className="mb-8 text-xl font-semibold text-slate-800">Provided Details</h3>
      <ProvidedDetailsContent providedDetails={providedDetails} />
    </div>
  </div>
);
